import type { DocumentData } from "firebase/firestore";
import { compactData, dateValue, stringList, stringValue } from "./firestore";

// Mirrors Models/SportModel.swift + Models/SBotUser.swift from the iOS app.

export type SBotUserRole =
  | "athlete"
  | "coach"
  | "admin"
  | "leagueManager"
  | "leagueStaff"
  | "organizationManager"
  | "organizationStaff";
export type SBotOnboardingStatus = "notStarted" | "pendingInvite" | "complete";
export type SBotMembershipTargetType = "organization" | "league";
export type SBotMembershipStatus = "active" | "pendingInvite" | "pendingApproval";
export type HomeDashboardCardID =
  | "leagueSchedule"
  | "organizations"
  | "athletes"
  | "leagueStaff"
  | "gpsTracking"
  | "broadcastOverlays"
  | "externalSources";

export const USER_ROLES: SBotUserRole[] = [
  "athlete",
  "coach",
  "admin",
  "leagueManager",
  "leagueStaff",
  "organizationManager",
  "organizationStaff",
];

export const ROLE_SORT_RANK: Record<SBotUserRole, number> = {
  admin: 0,
  leagueManager: 1,
  organizationManager: 2,
  leagueStaff: 3,
  organizationStaff: 4,
  coach: 5,
  athlete: 6,
};

export const HOME_DASHBOARD_CARD_IDS: HomeDashboardCardID[] = [
  "leagueSchedule",
  "organizations",
  "athletes",
  "leagueStaff",
  "gpsTracking",
  "broadcastOverlays",
  "externalSources",
];

// The two newest cards (broadcastOverlays, externalSources) are default-on
// only for league admins / SBot Labs admins; managers and organization admins
// receive them via visibleHomeCardIDs grants — so the implicit defaults below
// stay pinned to the original five.
const LEGACY_LEAGUE_MANAGER_DEFAULT_CARDS: HomeDashboardCardID[] = [
  "leagueSchedule",
  "organizations",
  "athletes",
  "leagueStaff",
  "gpsTracking",
];
const STAFF_DEFAULT_CARDS: HomeDashboardCardID[] = ["leagueSchedule", "gpsTracking"];

export const USER_ROLE_LABELS: Record<SBotUserRole, string> = {
  athlete: "Athlete",
  coach: "Coach",
  admin: "Admin",
  leagueManager: "League Manager",
  leagueStaff: "League Staff",
  organizationManager: "Organization Manager",
  organizationStaff: "Organization Staff",
};

export const HOME_DASHBOARD_CARD_LABELS: Record<HomeDashboardCardID, string> = {
  leagueSchedule: "League Schedule",
  organizations: "Organizations",
  athletes: "Athletes",
  leagueStaff: "League Staff",
  gpsTracking: "GPS Tracking",
  broadcastOverlays: "Broadcast Overlays",
  externalSources: "External Data",
};

export interface SBotAddress {
  line1: string;
  line2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
}

export interface SBotSocialProfile {
  platform: "instagram" | "x" | "tiktok" | "linkedin" | "website" | "other";
  handle?: string;
  url?: string;
}

export interface SBotMembership {
  targetType: SBotMembershipTargetType;
  targetID: string;
  role: SBotUserRole;
  status: SBotMembershipStatus;
  sportID?: string;
  leagueID?: string;
  organizationID?: string;
  displayName: string;
  inviteCode?: string;
  visibleHomeCardIDs?: HomeDashboardCardID[];
  expiresAt?: Date;
  supportGrantID?: string;
  grantedForUserID?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface SBotActiveContext {
  targetType: SBotMembershipTargetType;
  targetID: string;
  displayName: string;
  sportID?: string;
  leagueID?: string;
  organizationID?: string;
  role: SBotUserRole;
}

export interface SBotUser {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  photoStoragePath?: string;
  firstName: string;
  lastName: string;
  secondaryEmail?: string;
  phoneNumber?: string;
  bio?: string;
  address?: SBotAddress;
  socialProfiles: SBotSocialProfile[];
  favoriteSports: string[];
  favoriteTeams: string[];
  roles: SBotUserRole[];
  memberships: SBotMembership[];
  activeContext?: SBotActiveContext;
  onboardingStatus: SBotOnboardingStatus;
  /** Opaque ESPN-tracking preference blob managed by the iOS app; preserved verbatim. */
  sportsPreference?: Record<string, unknown>;
  createdAt?: Date;
  updatedAt?: Date;
  lastLoginAt?: Date;
}

export function membershipId(membership: SBotMembership): string {
  return `${membership.targetType}:${membership.targetID}:${membership.role}`;
}

export function resolvedLeagueID(membership: SBotMembership): string | undefined {
  if (membership.leagueID && membership.leagueID.length > 0) {
    return membership.leagueID;
  }
  return membership.targetType === "league" ? membership.targetID : undefined;
}

function isRole(value: unknown): value is SBotUserRole {
  return typeof value === "string" && (USER_ROLES as string[]).includes(value);
}

export function isHomeDashboardCardID(value: unknown): value is HomeDashboardCardID {
  return typeof value === "string" && (HOME_DASHBOARD_CARD_IDS as string[]).includes(value);
}

function isTargetType(value: unknown): value is SBotMembershipTargetType {
  return value === "organization" || value === "league";
}

function parseMembership(data: Record<string, unknown>): SBotMembership | null {
  const targetType = data.targetType;
  const targetID = data.targetID;
  const role = data.role;
  const displayName = data.displayName;
  if (!isTargetType(targetType) || typeof targetID !== "string" || !isRole(role) || typeof displayName !== "string") {
    return null;
  }

  const status = data.status;
  return {
    targetType,
    targetID,
    role,
    status: status === "pendingInvite" || status === "pendingApproval" ? status : "active",
    sportID: stringValue(data.sportID),
    leagueID: stringValue(data.leagueID),
    organizationID: stringValue(data.organizationID),
    displayName,
    inviteCode: stringValue(data.inviteCode),
    visibleHomeCardIDs: Array.isArray(data.visibleHomeCardIDs) ? stringList(data.visibleHomeCardIDs).filter(isHomeDashboardCardID) : undefined,
    expiresAt: dateValue(data.expiresAt),
    supportGrantID: stringValue(data.supportGrantID),
    grantedForUserID: stringValue(data.grantedForUserID),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
  };
}

export function parseActiveContext(data: unknown): SBotActiveContext | undefined {
  if (!data || typeof data !== "object") return undefined;
  const record = data as Record<string, unknown>;
  const targetType = record.targetType;
  const targetID = record.targetID;
  const displayName = record.displayName;
  const role = record.role;
  if (!isTargetType(targetType) || typeof targetID !== "string" || typeof displayName !== "string" || !isRole(role)) {
    return undefined;
  }
  return {
    targetType,
    targetID,
    displayName,
    sportID: stringValue(record.sportID),
    leagueID: stringValue(record.leagueID),
    organizationID: stringValue(record.organizationID),
    role,
  };
}

export function parseUser(id: string, data: DocumentData): SBotUser | null {
  const email = data.email;
  if (typeof email !== "string" || email.length === 0) {
    return null;
  }

  const onboardingRaw = data.onboardingStatus;
  const onboardingStatus: SBotOnboardingStatus =
    onboardingRaw === "pendingInvite" || onboardingRaw === "complete" || onboardingRaw === "notStarted"
      ? onboardingRaw
      : "notStarted";

  const socialProfiles = (Array.isArray(data.socialProfiles) ? data.socialProfiles : [])
    .filter((entry: unknown): entry is Record<string, unknown> => !!entry && typeof entry === "object")
    .flatMap((entry: Record<string, unknown>): SBotSocialProfile[] => {
      const platform = entry.platform;
      if (
        platform === "instagram" ||
        platform === "x" ||
        platform === "tiktok" ||
        platform === "linkedin" ||
        platform === "website" ||
        platform === "other"
      ) {
        return [{ platform, handle: stringValue(entry.handle), url: stringValue(entry.url) }];
      }
      return [];
    });

  return {
    id,
    email: email.toLowerCase(),
    displayName: stringValue(data.displayName),
    photoURL: stringValue(data.photoURL),
    photoStoragePath: stringValue(data.photoStoragePath),
    firstName: stringValue(data.firstName) ?? "",
    lastName: stringValue(data.lastName) ?? "",
    secondaryEmail: stringValue(data.secondaryEmail),
    phoneNumber: stringValue(data.phoneNumber),
    bio: stringValue(data.bio),
    address:
      data.address && typeof data.address === "object"
        ? {
            line1: stringValue((data.address as Record<string, unknown>).line1) ?? "",
            line2: stringValue((data.address as Record<string, unknown>).line2) ?? "",
            city: stringValue((data.address as Record<string, unknown>).city) ?? "",
            region: stringValue((data.address as Record<string, unknown>).region) ?? "",
            postalCode: stringValue((data.address as Record<string, unknown>).postalCode) ?? "",
            country: stringValue((data.address as Record<string, unknown>).country) ?? "",
          }
        : undefined,
    socialProfiles,
    favoriteSports: stringList(data.favoriteSports),
    favoriteTeams: stringList(data.favoriteTeams),
    roles: (Array.isArray(data.roles) ? data.roles : []).filter(isRole),
    memberships: (Array.isArray(data.memberships) ? data.memberships : [])
      .filter((entry: unknown): entry is Record<string, unknown> => !!entry && typeof entry === "object")
      .map(parseMembership)
      .filter((m: SBotMembership | null): m is SBotMembership => m !== null),
    activeContext: parseActiveContext(data.activeContext),
    onboardingStatus,
    sportsPreference:
      data.sportsPreference && typeof data.sportsPreference === "object"
        ? (data.sportsPreference as Record<string, unknown>)
        : undefined,
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
    lastLoginAt: dateValue(data.lastLoginAt),
  };
}

export function membershipFirestoreData(membership: SBotMembership): Record<string, unknown> {
  return compactData({
    targetType: membership.targetType,
    targetID: membership.targetID,
    role: membership.role,
    status: membership.status,
    displayName: membership.displayName,
    sportID: membership.sportID,
    leagueID: membership.leagueID,
    organizationID: membership.organizationID,
    inviteCode: membership.inviteCode,
    visibleHomeCardIDs: membership.visibleHomeCardIDs,
    expiresAt: membership.expiresAt,
    supportGrantID: membership.supportGrantID,
    grantedForUserID: membership.grantedForUserID,
    createdAt: membership.createdAt,
    updatedAt: membership.updatedAt,
  });
}

export function activeContextFirestoreData(context: SBotActiveContext): Record<string, unknown> {
  return compactData({
    targetType: context.targetType,
    targetID: context.targetID,
    displayName: context.displayName,
    role: context.role,
    sportID: context.sportID,
    leagueID: context.leagueID,
    organizationID: context.organizationID,
  });
}

/** Mirrors SBotUser.firestoreData(). Dates are written as-is; the SDK stores them as Timestamps. */
export function userFirestoreData(user: SBotUser): Record<string, unknown> {
  const addressIsEmpty =
    !user.address ||
    [user.address.line1, user.address.line2, user.address.city, user.address.region, user.address.postalCode, user.address.country].every(
      (part) => part.trim().length === 0,
    );

  return compactData({
    id: user.id,
    email: user.email.toLowerCase(),
    firstName: user.firstName,
    lastName: user.lastName,
    favoriteSports: user.favoriteSports,
    favoriteTeams: user.favoriteTeams,
    roles: user.roles,
    memberships: user.memberships.map(membershipFirestoreData),
    onboardingStatus: user.onboardingStatus,
    activeContext: user.activeContext ? activeContextFirestoreData(user.activeContext) : undefined,
    sportsPreference: user.sportsPreference,
    displayName: user.displayName,
    photoURL: user.photoURL,
    photoStoragePath: user.photoStoragePath,
    secondaryEmail: user.secondaryEmail,
    phoneNumber: user.phoneNumber,
    bio: user.bio,
    address: addressIsEmpty ? undefined : user.address ? { ...user.address } : undefined,
    socialProfiles: user.socialProfiles.length
      ? user.socialProfiles.map((profile) => compactData({ platform: profile.platform, handle: profile.handle, url: profile.url }))
      : undefined,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    lastLoginAt: user.lastLoginAt,
  });
}

// --- Derived helpers ported from SBotUser computed properties ---

// Port of RootView.MainTab.visibleTabs: the AI tab is hidden when the active
// membership is a staff-only role, unless the user is an SBot Labs admin.
export function canShowAITab(user: SBotUser | null | undefined, isSBotLabsAdmin: boolean): boolean {
  // Kept here as the stable UI-facing export; the league-scoped policy lives
  // in featureAccessPolicy so tools and feature routes use the same contract.
  if (isSBotLabsAdmin) return true;
  const membership = user ? activeMembership(user) : undefined;
  return !membership
    || (membership.role !== "leagueStaff"
      && membership.role !== "organizationManager"
      && membership.role !== "organizationStaff");
}

export function needsOnboarding(user: SBotUser): boolean {
  return user.onboardingStatus === "notStarted";
}

export function preferredName(user: SBotUser): string {
  if (user.displayName && user.displayName.length > 0) {
    return user.displayName;
  }
  const fullName = `${user.firstName} ${user.lastName}`.trim();
  if (fullName.length > 0) {
    return fullName;
  }
  return user.email.split("@")[0] ?? "SBot User";
}

export function initials(user: SBotUser): string {
  const parts = preferredName(user)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0] ?? "");
  const result = parts.join("").toUpperCase();
  return result.length > 0 ? result : "SB";
}

/** Temporary support-grant memberships expire locally before the server cleanup runs. */
export function isMembershipCurrentlyActive(membership: SBotMembership, now: Date = new Date()): boolean {
  return membership.status === "active" && (membership.expiresAt === undefined || membership.expiresAt.getTime() > now.getTime());
}

export function activeMembership(user: SBotUser): SBotMembership | undefined {
  const context = user.activeContext;
  if (context) {
    const match = user.memberships.find(
      (m) => isMembershipCurrentlyActive(m) && m.targetType === context.targetType && m.targetID === context.targetID && m.role === context.role,
    );
    if (match) return match;
  }
  return user.memberships.find((m) => isMembershipCurrentlyActive(m));
}

export function activeLeagueAdminMembership(user: SBotUser): SBotMembership | undefined {
  const membership = activeMembership(user);
  return membership && isActiveLeagueAdmin(membership) ? membership : undefined;
}

export function isActiveLeagueAdmin(membership: SBotMembership): boolean {
  return isMembershipCurrentlyActive(membership) && membership.role === "admin" && membership.targetType === "league" && !!resolvedLeagueID(membership);
}

export function isActiveLeagueManager(membership: SBotMembership): boolean {
  return isMembershipCurrentlyActive(membership) && membership.role === "leagueManager" && membership.targetType === "league" && !!resolvedLeagueID(membership);
}

export function isActiveLeagueManagement(membership: SBotMembership): boolean {
  return isActiveLeagueAdmin(membership) || isActiveLeagueManager(membership);
}

export function isActiveLeagueHomeDashboardEligible(membership: SBotMembership): boolean {
  return (
    isMembershipCurrentlyActive(membership) &&
    membership.targetType === "league" &&
    !!resolvedLeagueID(membership) &&
    (membership.role === "admin" || membership.role === "leagueManager" || membership.role === "leagueStaff")
  );
}

export function isActiveOrganizationAdmin(membership: SBotMembership): boolean {
  return isMembershipCurrentlyActive(membership) && membership.role === "admin" && membership.targetType === "organization" && !!resolvedLeagueID(membership);
}

export function isActiveOrganizationHomeDashboardEligible(membership: SBotMembership): boolean {
  return (
    isMembershipCurrentlyActive(membership) &&
    membership.targetType === "organization" &&
    !!resolvedLeagueID(membership) &&
    (membership.role === "admin" || membership.role === "organizationManager" || membership.role === "organizationStaff")
  );
}

export function isActiveHomeDashboardEligible(membership: SBotMembership): boolean {
  return isActiveLeagueHomeDashboardEligible(membership) || isActiveOrganizationHomeDashboardEligible(membership);
}

export function canShowGPSAssignmentsForMembership(membership: SBotMembership | undefined): boolean {
  return membership ? isActiveLeagueManagement(membership) : false;
}

export function canEditOrganizationBrandingForMembership(membership: SBotMembership | undefined): boolean {
  return membership ? isActiveOrganizationAdmin(membership) || isActiveLeagueAdmin(membership) : false;
}

export function configuredHomeDashboardCards(membership: SBotMembership): HomeDashboardCardID[] {
  if (membership.targetType === "league" && membership.role === "admin") {
    return HOME_DASHBOARD_CARD_IDS;
  }

  if (membership.targetType === "league" && membership.role === "leagueManager") {
    return membership.visibleHomeCardIDs ?? LEGACY_LEAGUE_MANAGER_DEFAULT_CARDS;
  }

  if (membership.targetType === "league" && membership.role === "leagueStaff") {
    return membership.visibleHomeCardIDs ?? STAFF_DEFAULT_CARDS;
  }

  if (membership.targetType === "organization" && membership.role === "admin") {
    return membership.visibleHomeCardIDs ?? [];
  }

  if (membership.targetType === "organization" && membership.role === "organizationManager") {
    return membership.visibleHomeCardIDs ?? LEGACY_LEAGUE_MANAGER_DEFAULT_CARDS;
  }

  if (membership.targetType === "organization" && membership.role === "organizationStaff") {
    return membership.visibleHomeCardIDs ?? STAFF_DEFAULT_CARDS;
  }

  return [];
}

export function visibleHomeDashboardCards(user: SBotUser | null | undefined): HomeDashboardCardID[] {
  const membership = user ? activeMembership(user) : undefined;
  return membership && isMembershipCurrentlyActive(membership) ? configuredHomeDashboardCards(membership) : [];
}

export function canShowHomeDashboardCard(
  cardID: HomeDashboardCardID,
  user: SBotUser | null | undefined,
  isSBotLabsAdmin = false,
): boolean {
  if (isSBotLabsAdmin) return true;
  return visibleHomeDashboardCards(user).includes(cardID);
}

export function activeLeagueManagementMembership(user: SBotUser): SBotMembership | undefined {
  const membership = activeMembership(user);
  return membership && isActiveLeagueManagement(membership) ? membership : undefined;
}

export function activeOrganizationAdminMembership(user: SBotUser): SBotMembership | undefined {
  const membership = activeMembership(user);
  return membership && isActiveOrganizationAdmin(membership) ? membership : undefined;
}

export function activeLeagueHomeDashboardMembership(user: SBotUser): SBotMembership | undefined {
  const membership = activeMembership(user);
  return membership && isActiveLeagueHomeDashboardEligible(membership) ? membership : undefined;
}

export function activeOrganizationHomeDashboardMembership(user: SBotUser): SBotMembership | undefined {
  const membership = activeMembership(user);
  return membership && isActiveOrganizationHomeDashboardEligible(membership) ? membership : undefined;
}

export function activeHomeDashboardMembership(user: SBotUser): SBotMembership | undefined {
  return activeLeagueHomeDashboardMembership(user) ?? activeOrganizationHomeDashboardMembership(user);
}

export const canManageLeagueSettings = (user: SBotUser) => activeLeagueAdminMembership(user) !== undefined;
export const canManageLeagueInvitations = (user: SBotUser) => activeLeagueAdminMembership(user) !== undefined;
export const canManageLeagueOrganizationAdminData = (user: SBotUser) => activeLeagueAdminMembership(user) !== undefined;
export const canManageLeagueAthleteData = (user: SBotUser) => activeLeagueManagementMembership(user) !== undefined;
export const canManageLeagueCompetitionData = (user: SBotUser) => activeLeagueManagementMembership(user) !== undefined;
